import React, { FunctionComponent, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "../../config/auth.config";
import FullpageSpinner from "./FullpageSpinner";

export enum UserType {
  UNDEFINED = -1,
  GUEST = 0,
  STANDBYUSER = 1,
  UPFITTER = 2,
  ENDCUSTOMER = 3,
}

interface ProtectedRouteProps {
  children: React.ReactElement;
  allowedRoles: UserType[];
  redirectTo?: string;
}

/**
 * ProtectedRoute component that checks if the authenticated user has the required role.
 * If not, redirects to the specified path (default: /AccessDenied).
 */
const ProtectedRoute: FunctionComponent<ProtectedRouteProps> = ({
  children,
  allowedRoles,
  redirectTo = "/AccessDenied",
}) => {
  const { instance, accounts } = useMsal();
  const [userType, setUserType] = useState<UserType | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (accounts.length > 0) {
      instance
        .acquireTokenSilent({
          ...loginRequest,
          account: accounts[0],
        })
        .then((response) => {
          const claims = response.account.idTokenClaims as any;
          const accessTokenClaims = response.idTokenClaims as any;

          // Support both B2C and Entra ID claim formats
          const userEnum =
            accessTokenClaims?.extension_UserEnum ||
            accessTokenClaims?.UserEnum ||
            accessTokenClaims?.UserType ||
            claims?.extension_UserEnum ||
            claims?.UserEnum ||
            claims?.UserType ||
            claims?.roles?.[0] ||
            accessTokenClaims?.roles?.[0];

          let detectedUserType: UserType = UserType.UNDEFINED;

          if (typeof userEnum === "number") {
            detectedUserType = userEnum as UserType;
          } else if (typeof userEnum === "string") {
            switch (userEnum.toUpperCase()) {
              case "STANDBYUSER":
              case "1":
                detectedUserType = UserType.STANDBYUSER;
                break;
              case "UPFITTER":
              case "UPPFITTER":
              case "2":
                detectedUserType = UserType.UPFITTER;
                break;
              case "ENDCUSTOMER":
              case "ENDUSER":
              case "3":
                detectedUserType = UserType.ENDCUSTOMER;
                break;
              case "GUEST":
              case "0":
                detectedUserType = UserType.GUEST;
                break;
            }
          }

          setUserType(detectedUserType);
          setLoading(false);
        })
        .catch((e) => {
          console.log("Token acquisition failed in ProtectedRoute:", e);
          setUserType(UserType.UNDEFINED);
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, [accounts, instance]);

  if (loading) {
    return <FullpageSpinner text="Checking permissions..." />;
  }

  if (userType === null || userType === UserType.UNDEFINED) {
    return <Navigate to="/AccessDenied" replace />;
  }

  if (!allowedRoles.includes(userType)) {
    return <Navigate to={redirectTo} replace />;
  }

  return children;
};

export default ProtectedRoute;
